// src/components/admin/BHReconcile.jsx
import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useLocation } from '../../contexts/LocationContext';

const normalize = (s) => (s || '').trim().toLowerCase();

const parseBHList = (text) => {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const students = [];

  for (const line of lines) {
    // Skip header lines and summary lines
    if (
      line.startsWith('Total') ||
      line.startsWith('Care Recipient') ||
      line.startsWith('Reservations') ||
      line === ''
    ) continue;

    // Tab-separated: Care Recipient | Age | Employee Name | Drop-off | Pick-up | Reservation # | Status | Date Confirmed
    const cols = line.split('\t');
    if (cols.length < 6) continue;

    const name = cols[0].trim();
    const reservationNum = cols[5].trim();
    const status = cols[6]?.trim();

    if (!name || !reservationNum) continue;
    if (status && status.toLowerCase() === 'canceled') continue;

    // Split name into first/last
    const nameParts = name.trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    students.push({ name, firstName, lastName, reservationNum });
  }

  return students;
};

const formatDateMMDDYY = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = String(date.getFullYear()).slice(-2);
  return `${m}-${d}-${y}`;
};

const BHReconcile = ({ onBack }) => {
  const { currentLocation } = useLocation();
  const [pastedText, setPastedText] = useState('');
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [results, setResults] = useState(null);

  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixLog, setFixLog] = useState([]);
  const [selectedToRemove, setSelectedToRemove] = useState(new Set());

// Reset selections when location changes
  React.useEffect(() => {
    setSelectedToRemove(new Set());
  }, [currentLocation]);

  const handleReconcile = async () => {
    if (!pastedText.trim()) {
      alert('Please paste the BH confirmed list first.');
      return;
    }
    setLoading(true);
    setResults(null);
    setFixLog([]);

    try {
      const bhStudents = parseBHList(pastedText);
      if (bhStudents.length === 0) {
        alert('Could not parse any students from the pasted text. Make sure you copied the full table from BH portal.');
        setLoading(false);
        return;
      }

      const checkDate = new Date(selectedDate + 'T00:00:00');
      const dateKey = formatDateMMDDYY(checkDate);

      // Load all BH students from Firestore
      const snap = await getDocs(collection(db, 'students'));
      const allStudents = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter by current location — Redmond only sees Redmond students, Kirkland only sees Kirkland
      const bhFirestore = allStudents.filter(s => {
        if (s.archived) return false;
        const isBH = s.billingType === 'bright-horizons' ||
          (s.billingType === 'third-party' &&
           (s.programSelection || '').toLowerCase().includes('bright horizons'));
        if (!isBH) return false;
        if (!s.careDates?.some(c => c.date === dateKey)) return false;
        // Location filter — 'all' shows everything, otherwise match locationId
        if (currentLocation === 'all') return true;
        return (s.locationId || 'redmond') === currentLocation;
      });

      // Match BH list against Firestore
      const matched = [];
      const inBHNotInApp = [];
      const inAppNotInBH = [];

      // For each BH confirmed student, try to find in Firestore
      bhStudents.forEach(bhS => {
        const found = bhFirestore.find(fs =>
          normalize(fs.firstName) === normalize(bhS.firstName) &&
          normalize(fs.lastName) === normalize(bhS.lastName)
        );
        if (found) {
          matched.push({ bh: bhS, app: found });
        } else {
          inBHNotInApp.push(bhS);
        }
      });

      // For each Firestore student, check if in BH list
      bhFirestore.forEach(fs => {
        const found = bhStudents.find(bhS =>
          normalize(bhS.firstName) === normalize(fs.firstName) &&
          normalize(bhS.lastName) === normalize(fs.lastName)
        );
        if (!found) {
          inAppNotInBH.push(fs);
        }
      });

      setResults({
        date: checkDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
        dateKey,
        bhCount: bhStudents.length,
        matched,
        inBHNotInApp,
        inAppNotInBH,
      });
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFixExtras = async (studentsToFix) => {
    if (!studentsToFix || studentsToFix.length === 0) return;
    if (!window.confirm(`Remove ${results.dateKey} from ${studentsToFix.length} selected student(s)? This cannot be undone.`)) return;

    setFixing(true);
    const log = [];

    for (const student of studentsToFix) {
      try {
        const updatedCareDates = (student.careDates || []).filter(c => c.date !== results.dateKey);
        await updateDoc(doc(db, 'students', student.id), {
          careDates: updatedCareDates,
          lastUpdated: new Date(),
        });
        log.push({ name: `${student.firstName} ${student.lastName}`, status: '✅ Removed', color: '#059669' });
      } catch (err) {
        log.push({ name: `${student.firstName} ${student.lastName}`, status: `❌ Failed: ${err.message}`, color: '#dc2626' });
      }
    }

    setFixLog(log);
    setFixing(false);

    // Refresh results
    await handleReconcile();
  };

  return (
    <div style={{ padding: '1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '7px', cursor: 'pointer', fontSize: '0.85rem', color: '#374151', marginBottom: '1rem' }}>
          <ArrowLeft size={15} /> Back to Dashboard
        </button>
        <h1 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>
          BH Daily Reconciliation
        </h1>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
          Paste the BH confirmed list to find and fix mismatches with your app roster
        </p>
        <p style={{ margin: '0.4rem 0 0' }}>
          <span style={{ backgroundColor: currentLocation === 'kirkland' ? '#d1fae5' : '#dbeafe', color: currentLocation === 'kirkland' ? '#065f46' : '#1e40af', padding: '0.2rem 0.6rem', borderRadius: '999px', fontWeight: '700', fontSize: '0.78rem' }}>
            📍 {currentLocation === 'kirkland' ? 'Kirkland' : currentLocation === 'all' ? 'All Locations' : 'Redmond'} — only this location's BH students will be affected
          </span>
        </p>
      </div>

      {/* Input Card */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'end', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#374151', marginBottom: '0.35rem' }}>
              Date to reconcile
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              style={{ padding: '0.5rem 0.75rem', border: '1.5px solid #d1d5db', borderRadius: '7px', fontSize: '0.875rem', color: '#374151' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#374151', marginBottom: '0.35rem' }}>
            Paste BH confirmed list
            <span style={{ fontWeight: '400', color: '#9ca3af', marginLeft: '0.5rem' }}>
              Copy the full table from BH portal → Ctrl+A → Ctrl+C → paste here
            </span>
          </label>
          <textarea
            value={pastedText}
            onChange={e => setPastedText(e.target.value)}
            placeholder={`Total Confirmed = 25\nCare Recipient\tAge\tEmployee Name\tDrop-off Time\tPick-up Time\tReservation #\tStatus\tDate Confirmed\nMiraya Garg\t5yrs\tAastha Channa\t09:00\t16:45\t100-3KHD-ECV7G\tConfirmed\t06/04/2026\n...`}
            style={{
              width: '100%', minHeight: '140px', padding: '0.75rem',
              border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem',
              fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
              color: '#374151', backgroundColor: '#fafafa',
            }}
          />
        </div>

        <button
          onClick={handleReconcile}
          disabled={loading || !pastedText.trim()}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.65rem 1.5rem', backgroundColor: loading || !pastedText.trim() ? '#9ca3af' : '#1A2E4A',
            color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700',
            fontSize: '0.9rem', cursor: loading || !pastedText.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Reconciling...' : 'Reconcile'}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div>
          {/* Summary Strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'BH Confirmed', value: results.bhCount, color: '#1A2E4A', icon: '📋' },
              { label: 'Matched', value: results.matched.length, color: '#059669', icon: '✅' },
              { label: 'In BH, not in app', value: results.inBHNotInApp.length, color: '#d97706', icon: '⚠️' },
              { label: 'In app, not in BH', value: results.inAppNotInBH.length, color: '#dc2626', icon: '🗑️' },
            ].map(stat => (
              <div key={stat.label} style={{ backgroundColor: 'white', borderRadius: '10px', padding: '0.85rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `3px solid ${stat.color}` }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '800', color: stat.color, lineHeight: 1 }}>{stat.icon} {stat.value}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.2rem', fontWeight: '600' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>
            Reconciling for <strong>{results.date}</strong> ({results.dateKey})
          </p>

          {/* Matched */}
          {results.matched.length > 0 && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #bbf7d0', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <CheckCircle size={18} style={{ color: '#059669' }} />
                <span style={{ fontWeight: '700', color: '#065f46', fontSize: '0.9rem' }}>Matched — {results.matched.length} students</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {results.matched.map(({ bh }) => (
                  <span key={bh.name} style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '5px', padding: '0.25rem 0.6rem', fontSize: '0.8rem', color: '#065f46' }}>
                    {bh.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* In BH not in app */}
          {results.inBHNotInApp.length > 0 && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #fde68a', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <AlertTriangle size={18} style={{ color: '#d97706' }} />
                <span style={{ fontWeight: '700', color: '#92400e', fontSize: '0.9rem' }}>In BH but not in app — {results.inBHNotInApp.length} students</span>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#92400e', marginBottom: '0.75rem' }}>
                These students are confirmed by BH but missing from your roster. Check if their registration form was completed or reprocess their BH email.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {results.inBHNotInApp.map(s => (
                  <div key={s.name} style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#92400e', display: 'flex', justifyContent: 'space-between' }}>
                    <span><strong>{s.name}</strong></span>
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Reservation: {s.reservationNum}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* In app not in BH */}
          {results.inAppNotInBH.length > 0 && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #fecaca', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <XCircle size={18} style={{ color: '#dc2626' }} />
                  <span style={{ fontWeight: '700', color: '#991b1b', fontSize: '0.9rem' }}>In app but not in BH — {results.inAppNotInBH.length} students</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      if (selectedToRemove.size === results.inAppNotInBH.length) {
                        setSelectedToRemove(new Set());
                      } else {
                        setSelectedToRemove(new Set(results.inAppNotInBH.map(s => s.id)));
                      }
                    }}
                    style={{ padding: '0.35rem 0.75rem', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '7px', fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer', color: '#374151' }}
                  >
                    {selectedToRemove.size === results.inAppNotInBH.length ? 'Deselect all' : 'Select all'}
                  </button>
                  <button
                    onClick={() => handleFixExtras(results.inAppNotInBH.filter(s => selectedToRemove.has(s.id)))}
                    disabled={fixing || selectedToRemove.size === 0}
                    style={{ padding: '0.45rem 1rem', backgroundColor: fixing || selectedToRemove.size === 0 ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.82rem', cursor: fixing || selectedToRemove.size === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    {fixing ? 'Removing...' : selectedToRemove.size > 0 ? `Remove ${results.dateKey} from ${selectedToRemove.size} selected` : 'Select students to remove'}
                  </button>
                </div>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                Check each student before removing — select individually or use "Select all".
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {results.inAppNotInBH.map(s => (
                  <label key={s.id} style={{ backgroundColor: selectedToRemove.has(s.id) ? '#fef2f2' : '#fff', border: `1px solid ${selectedToRemove.has(s.id) ? '#fca5a5' : '#e5e7eb'}`, borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedToRemove.has(s.id)}
                      onChange={e => {
                        const next = new Set(selectedToRemove);
                        if (e.target.checked) next.add(s.id);
                        else next.delete(s.id);
                        setSelectedToRemove(next);
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <strong style={{ flex: 1 }}>{s.firstName} {s.lastName}</strong>
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{s.billingType}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

{/* Fix log */}
          {fixLog.length > 0 && (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#374151', marginBottom: '0.5rem' }}>Fix Results</div>
              {fixLog.map((entry, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: entry.color, padding: '0.2rem 0' }}>
                  {entry.status} {entry.name}
                </div>
              ))}
            </div>
          )}

          {/* Persistent action needed banner */}
          {results?.inBHNotInApp?.length > 0 && (
            <div style={{ backgroundColor: '#fffbeb', borderRadius: '12px', border: '1.5px solid #fde68a', padding: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#92400e', marginBottom: '0.5rem' }}>
                ⚠️ Action Needed — {results.inBHNotInApp.length} student(s) confirmed by BH but missing from app
              </div>
              <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0 0 0.5rem' }}>
                Check if their BH email was processed or ask them to complete the Stemtree registration form.
              </p>
              {results.inBHNotInApp.map(s => (
                <div key={s.name} style={{ fontSize: '0.82rem', color: '#92400e', padding: '0.2rem 0', display: 'flex', justifyContent: 'space-between' }}>
                  <strong>{s.name}</strong>
                  <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Reservation: {s.reservationNum}</span>
                </div>
              ))}
            </div>
          )}

          {results.inAppNotInBH.length === 0 && results.inBHNotInApp.length === 0 && (
            <div style={{ backgroundColor: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
              <div style={{ fontWeight: '800', color: '#065f46', fontSize: '1rem' }}>Perfect match!</div>
              <div style={{ color: '#374151', fontSize: '0.85rem', marginTop: '0.25rem' }}>App roster matches BH confirmed list exactly.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BHReconcile;
